// The cloud field descriptor: the one table that says what a cloud config is made of. Ported from
// the stats-page cloud lib so the grammar in lib/report-params.js needs nothing from the page.
//
// Per field:
//   urlName            two-letter name it carries in the wc<id> param
//   expName            name it carries in an exported preset bag
//   type / subtype     how the stored token is cast back
//   prefix             dropped when writing, restored when reading (colours lose their '#')
//   mapTable + indices a value stored as a short token, resolved through one of the tables below
//   ignoreValues       values that mean "say nothing", so the token is left out
//   presetDependable   false when a named preset does NOT answer for the field
//   filter/On/Off      fields that share one grouped token, one letter each for on and off
//
// A field absent from here is not addressable in a param: `junk` is config-only by design.
//
// mapTable rows are [value, label, token] and are indexed positionally (mapFromIndex /
// mapToIndex), so row order and token letters are part of the stored format -- never renumber
// them, and only ever append.

import { FONTS } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/fonts'
import { PRESETS } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/presets'
import { DEFAULT_CONFIG } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/config'

// how a word's weight becomes its drawn size
export const LAYOUTS = [
  ['log', 'log', '1'],
  ['linear', 'n', '2'],
  ['sqrt', '√n', '3'],
  ['pow2', 'pow 0.2', '4'],
  ['pow3', 'pow 0.3', '5'],
  ['pow4', 'pow 0.4', '6'],
  ['pow6', 'pow 0.6', '7'],
  ['pow7', 'pow 0.7', '8'],
  ['pow8', 'pow 0.8', '9'],
  ['pow9', 'pow 0.9', 'a'],
  ['pow12', 'pow 1.2', 'b'],
  ['pow20', 'pow 2', 'c'],
  ['pow50', 'pow 5', 'd']
]

// how the palette is spread across the words
export const COLOR_ALGORITHMS = [
  ['adj_levels', 'Adjusted Levels', '1'],
  ['levels', 'Levels', '2'],
  ['gradient', 'Gradient', '3'],
  ['circle', 'Circle', '4']
]

// the shapes a cloud can be poured into; BASIC_MASKS names the ones needing no svg path
export const MASK_OPTIONS = [
  ['heart', 'Heart', '2'],
  ['shield', 'Shield', '6'],
  ['leaf', 'Leaf', '7'],
  ['circle', 'Circle', '3'],
  ['square', 'Square', '5'],
  ['diamond', 'Diamond', '4'],
  ['dog1', 'Dog 1', 'd'],
  ['dog2', 'Dog 2', 'e'],
  ['bird1', 'Bird 1', 'c'],
  ['bird2', 'Bird 2', 'g'],
  ['rabbit', 'Rabbit', 'f'],
  ['butterfly', 'Butterfly', '1'],
  ['lion', 'Lion', 'a'],
  ['cammel', 'Camel', 'b'],
  ['elephant', 'Elephant', '8'],
  ['shark', 'Shark', '9']
]

// [[from, to], contrastingTextColour] -- a gradient colour style picks one of these by index
export const GRADIENTS = [
  [['#8CC63F', '#003863'], '#000000'],
  [['#f4fad5', '#91cf00'], '#000000'],
  [['#00bbff', '#0081b0'], '#ffffff'],
  [['#FCD921', '#6E272D'], '#000000'],
  [['#ffe600', '#ff6600'], '#000000'],
  [['#d5aeeb', '#691c4a'], '#000000'],
  [['#CCCCCC', '#333333'], '#000000']
]

// every field sharing this token collapses into one, so the group is written once
const WORD_FILTER = 'wf'

// a text-processing field: one letter for on, one for off, and no named preset answers for it
const wordFilter = (expName, filterOn, filterOff) => ({
  filter: WORD_FILTER,
  filterOn,
  filterOff,
  expName,
  presetDependable: false,
  useRawData: false
})

// a short token resolved through one of the tables above
const mapped = (urlName, expName, mapTable, extra = {}) => ({
  urlName,
  expName,
  mapTable,
  mapFromIndex: 0,
  mapToIndex: 2,
  type: 'string',
  ...extra
})

export const SYNC = {
  currentPresetId: {
    urlName: 'ps',
    expName: 'preset',
    ignoreValues: ['-1', undefined],
    type: 'string',
    presetDependable: false
  },
  scaleFunction: mapped('sf', 'scaleFunction', 'layouts'),
  colorStyle: mapped('cf', 'colorStyle', 'colorsAlgorithm'),
  blackList: {
    urlName: 'bl',
    expName: 'blackList',
    type: 'array',
    subtype: 'string',
    presetDependable: false,
    useRawData: false
  },
  mask: mapped('mk', 'maskName', 'masks', { ignoreValues: ['', undefined], presetDependable: false }),
  customMaskId: {
    urlName: 'mc',
    expName: 'maskId',
    ignoreValues: ['-1', undefined],
    type: 'string',
    presetDependable: false
  },
  customMaskPath: {
    urlName: 'mp',
    expName: 'maskPath',
    ignoreValues: ['', undefined],
    type: 'string',
    presetDependable: false
  },
  activeWord: { urlName: 'cw', expName: 'activeWord', type: 'string', presetDependable: false },
  randIndex: { urlName: 'ri', expName: 'rIndex', type: 'number', presetDependable: false },
  background: { urlName: 'bg', expName: 'background', prefix: '#', type: 'string' },
  orientation: { urlName: 'or', expName: 'orientation', type: 'string' },
  rangeStart: { urlName: 'rs', expName: 'rangeStart', type: 'number' },
  rangeEnd: { urlName: 're', expName: 'rangeEnd', type: 'number' },
  angles: { urlName: 'as', expName: 'angles', type: 'array', subtype: 'number' },
  colors: { urlName: 'cs', expName: 'colors', prefix: '#', type: 'array', subtype: 'string' },
  maxWords: { urlName: 'mw', expName: 'maxWords', type: 'number', useRawData: false },
  fontFamily: mapped('ff', 'font', 'fonts'),
  spell: wordFilter('spell', 'a', 'b'),
  caps: wordFilter('caps', 'c', 'd'),
  mergeWords: wordFilter('mergeWords', 'e', 'f'),
  twoWords: wordFilter('twoWords', 'g', 'h'),
  stop1: wordFilter('stop1', 'k', 'l'),
  stop2: wordFilter('stop2', 'm', 'n'),
  stop3: wordFilter('stop3', 'o', 'p'),
  stop4: wordFilter('stop4', 'r', 's'),
  stop5: wordFilter('stop5', 't', 'u')
}

/**
 * The descriptor bundle the shared cloud grammar takes as its `lib`.
 *
 * Key names are load-bearing: `lib[field.mapTable]` resolves a mapped field against the table its
 * descriptor names, so `layouts` / `colorsAlgorithm` / `masks` / `fonts` have to be spelled exactly
 * as the mapTable values above.
 *
 * `presetConfig` returns the raw PRESETS row, NOT the named object `getPresetById` builds -- the
 * grammar reads the look out of it positionally (`preset[1][…]`).
 */
export const WC_LIB = {
  sync: SYNC,
  defaults: DEFAULT_CONFIG,
  layouts: LAYOUTS,
  colorsAlgorithm: COLOR_ALGORITHMS,
  masks: MASK_OPTIONS,
  fonts: FONTS,
  gradients: GRADIENTS,
  presets: PRESETS,
  presetConfig: id => PRESETS.find(p => p[2] === id) || null,
  colorStyleIndex: id => COLOR_ALGORITHMS.findIndex(ca => ca[0] === id)
}
